/**
 * Motion primitives for the sign ceremony (WP-F7).
 *
 * Pure + React-free where it can be, so the easing and the timings are unit-tested directly rather
 * than inferred from a rendered animation.
 *
 * **Why this module gates framer-motion.** The global `prefers-reduced-motion` killswitch in
 * `index.css` zeroes CSS `animation-duration`/`transition-duration` — but framer-motion does not
 * animate with CSS. It drives the Web Animations API / rAF from JS, so the stylesheet cannot stop
 * it and honouring the preference has to happen in JS, before the library is even imported. That is
 * what `usePrefersReducedMotion` is for: the seal's caller reads it FIRST and simply never reaches
 * the dynamic import when motion is refused. A referee who asked for no motion pays nothing — not a
 * stilled animation, not the ~34kB gzip of an animation library they will never see run.
 */
import { useSyncExternalStore } from 'react';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function subscribeToReducedMotion(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const query = window.matchMedia(REDUCED_MOTION_QUERY);
  query.addEventListener('change', onStoreChange);
  return () => query.removeEventListener('change', onStoreChange);
}

function readReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.(REDUCED_MOTION_QUERY).matches;
}

function readServerReducedMotion(): boolean {
  return false;
}

/**
 * The ceremonial beat: 450ms for the seal stamp (design-spec/03 — "the one place motion is allowed
 * to be theatrical"). Exported so the ceremony and its tests agree on one number.
 */
export const SEAL_STAMP_MS = 450;

/**
 * How long the Sign screen holds after a successful signature before handing over to Done: the
 * stamp, plus a beat to let it land and be read as final. Under reduced motion this is 0 — there is
 * nothing to watch, and making someone wait for an animation they opted out of is the same
 * disrespect as playing it.
 */
export const CEREMONY_HOLD_MS = 1000;

/**
 * GSAP's `back.out(1.7)`, exactly — the overshoot the design spec names for the seal stamp.
 *
 * Ported rather than approximated with a cubic-bezier: the spec gives a specific overshoot constant
 * and `back.out(s)` has a closed form, so there is no reason to eyeball it. GSAP's Back.easeOut is
 *   f(t) = (t-1)² · ((s+1)(t-1) + s) + 1
 * with `s` the overshoot. At s = 1.7 this peaks ≈1.098 around t ≈ 0.6 and settles to exactly 1 —
 * the "stamp presses past and settles" feel, which a plain ease-out cannot produce because it never
 * exceeds 1.
 *
 * Kept as a plain function of t so framer-motion takes it as a custom `ease` and so the tests can
 * assert the curve's actual properties (starts 0, ends 1, overshoots >1) rather than trusting a
 * bezier's four magic numbers.
 */
export const BACK_OUT_OVERSHOOT = 1.7;

export function backOut(t: number, overshoot: number = BACK_OUT_OVERSHOOT): number {
  const p = t - 1;
  return p * p * ((overshoot + 1) * p + overshoot) + 1;
}

/**
 * Does this visitor want motion suppressed?
 *
 * Live, not read-once: the OS-level toggle can flip while the page is open, and `change` is the
 * only way to hear about it. Defaults to `false` (motion allowed) where `matchMedia` is missing —
 * the same default the CSS media query implies — but note the CALLER's obligation is the reverse:
 * treat `true` as "never import the animation", not "import it and set duration 0".
 */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribeToReducedMotion, readReducedMotion, readServerReducedMotion);
}
